import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PerformanceSummary } from '@/hooks/usePartnerAnalytics';
import { RatingDisplay } from '@/components/partners/RatingDisplay';
import { PrequalificationStatusBadge } from '@/components/partners/PrequalificationStatusBadge';
import { Button } from '@/components/ui/button';
import { Building2, FileText, Star, Calendar } from 'lucide-react';
import { format } from 'date-fns';

interface ContractorDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractor: PerformanceSummary | null;
}

export function ContractorDetailSheet({ open, onOpenChange, contractor }: ContractorDetailSheetProps) {
  if (!contractor) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {contractor.name}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="h-4 w-4" />
                Performance Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Overall Rating</span>
                <RatingDisplay rating={contractor.overall_rating} size="md" />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Safety Rating</span>
                <RatingDisplay rating={contractor.safety_rating} size="sm" />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Quality Rating</span>
                <RatingDisplay rating={contractor.quality_rating} size="sm" />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Reliability Rating</span>
                <RatingDisplay rating={contractor.reliability_rating} size="sm" />
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm text-muted-foreground">Total Ratings</span>
                <span className="font-medium">{contractor.total_ratings}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Document Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Prequalification</span>
                <PrequalificationStatusBadge status={contractor.prequalification_status} />
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{contractor.verified_documents}</div>
                  <div className="text-xs text-muted-foreground">Verified</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{contractor.pending_documents}</div>
                  <div className="text-xs text-muted-foreground">Pending</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{contractor.rejected_documents}</div>
                  <div className="text-xs text-muted-foreground">Rejected</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {contractor.last_rating_date && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Last Rating</span>
                  <span className="text-sm font-medium">
                    {format(new Date(contractor.last_rating_date), 'MMM dd, yyyy')}
                  </span>
                </div>
              )}
              {contractor.last_document_upload && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Last Document Upload</span>
                  <span className="text-sm font-medium">
                    {format(new Date(contractor.last_document_upload), 'MMM dd, yyyy')}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button className="flex-1" variant="outline">View Full Profile</Button>
            <Button className="flex-1">Add Rating</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
